/**
 * ComplianceLensCard - Privacy Compliance Lens Summary Card
 * ============================================================================
 * DC-821: Card component displaying Privacy Compliance Lens assessment results
 *
 * Displays:
 * - Overall compliance score with visual gauge
 * - Compliance level badge (compliant/partial/non_compliant/not_applicable)
 * - Regulation breakdown (GDPR/HIPAA/CCPA) with mini progress bars
 * - Findings summary by severity
 * - Quick actions (run assessment, view details)
 *
 * Design: Clean, professional card optimized for compliance context
 *
 * @version 1.0.0
 * @created 2025-12-26
 * ============================================================================
 */

import React from 'react';
import {
  useComplianceLens,
  ComplianceLensResult,
  ComplianceLevel,
} from './useComplianceLens';
import './compliance-lens-card.css';

// ============================================================================
// Types
// ============================================================================

export interface ComplianceLensCardProps {
  /** Journey ID for API calls */
  journeyId: string;
  /** Pre-loaded result data (optional) */
  result?: ComplianceLensResult;
  /** Callback when "Run Assessment" is clicked */
  onRunAssessment?: () => void;
  /** Callback when "View Details" is clicked */
  onViewDetails?: (assessmentId: string) => void;
  /** Callback when findings badge is clicked */
  onViewFindings?: (severity?: string) => void;
  /** Show compact mode for sidebar/small spaces */
  compact?: boolean;
  /** Show loading state (external control) */
  loading?: boolean;
}

// ============================================================================
// Constants
// ============================================================================

const COMPLIANCE_LEVELS: Record<ComplianceLevel, { label: string; color: string; description: string }> = {
  compliant: {
    label: 'COMPLIANT',
    color: 'var(--tri-good)',
    description: 'Meets all checked requirements',
  },
  partial: {
    label: 'PARTIAL',
    color: 'var(--tri-soft)',
    description: 'Minor gaps, low-risk findings only',
  },
  non_compliant: {
    label: 'NON-COMPLIANT',
    color: 'var(--tri-hard)',
    description: 'Significant gaps requiring remediation',
  },
  not_applicable: {
    label: 'N/A',
    color: 'var(--tri-slate)',
    description: 'Regulation not applicable to this data',
  },
};

const REGULATION_INFO: Record<string, { name: string; fullName: string }> = {
  gdpr: { name: 'GDPR', fullName: 'EU General Data Protection Regulation' },
  hipaa: { name: 'HIPAA', fullName: 'Health Insurance Portability and Accountability Act' },
  ccpa: { name: 'CCPA', fullName: 'California Consumer Privacy Act' },
};

// ============================================================================
// Helper Functions
// ============================================================================

function getScoreColor(score: number): string {
  if (score >= 90) return 'var(--tri-good)';
  if (score >= 70) return 'var(--tri-soft)';
  return 'var(--tri-hard)';
}

function getLevelColor(level?: string): string {
  if (!level) return 'var(--tri-slate)';
  const info = COMPLIANCE_LEVELS[level as ComplianceLevel];
  return info?.color || 'var(--tri-slate)';
}

function formatDate(dateString?: string): string {
  if (!dateString) return 'Never';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// ============================================================================
// Sub-Components
// ============================================================================

interface ScoreGaugeProps {
  score: number;
  size?: 'small' | 'medium' | 'large';
}

const ScoreGauge: React.FC<ScoreGaugeProps> = ({ score, size = 'medium' }) => {
  const sizes = {
    small: { width: 60, strokeWidth: 4, fontSize: 14 },
    medium: { width: 100, strokeWidth: 6, fontSize: 24 },
    large: { width: 140, strokeWidth: 8, fontSize: 32 },
  };

  const { width, strokeWidth } = sizes[size];
  const radius = (width - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const progress = (score / 100) * circumference;
  const color = getScoreColor(score);

  return (
    <div className="compliance-score-gauge" style={{ width, height: width }}>
      <svg width={width} height={width} viewBox={`0 0 ${width} ${width}`}>
        {/* Background circle */}
        <circle
          cx={width / 2}
          cy={width / 2}
          r={radius}
          fill="none"
          stroke="var(--tri-taupe)"
          strokeWidth={strokeWidth}
        />
        {/* Progress circle */}
        <circle
          cx={width / 2}
          cy={width / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeDasharray={`${progress} ${circumference}`}
          strokeLinecap="round"
          transform={`rotate(-90 ${width / 2} ${width / 2})`}
          style={{ transition: 'stroke-dasharray 0.5s ease' }}
        />
      </svg>
      <div className="compliance-score-value">
        <span className="score-num">{Math.round(score)}</span>
        <span className="score-max">/100</span>
      </div>
    </div>
  );
};

interface RegulationCardProps {
  regulation: 'gdpr' | 'hipaa' | 'ccpa';
  score?: number;
  level?: string;
}

const RegulationCard: React.FC<RegulationCardProps> = ({ regulation, score, level }) => {
  const info = REGULATION_INFO[regulation];
  const hasData = score !== undefined && score !== null;
  const displayScore = hasData ? Math.round(score) : '--';
  const displayLevel = level || 'not_applicable';
  const color = getLevelColor(displayLevel);

  return (
    <div className="regulation-card" title={info.fullName}>
      <div className="reg-name">{info.name}</div>
      <div className={`reg-score ${displayLevel}`}>{displayScore}</div>
      <div className="reg-bar">
        <div
          className="reg-bar-fill"
          style={{
            width: hasData ? `${score}%` : '0%',
            backgroundColor: color,
          }}
        />
      </div>
      <div className="reg-level">{displayLevel.replace('_', ' ')}</div>
    </div>
  );
};

interface FindingsBadgeProps {
  severity: 'critical' | 'major' | 'minor' | 'observation';
  count: number;
  onClick?: () => void;
}

const FindingsBadge: React.FC<FindingsBadgeProps> = ({ severity, count, onClick }) => {
  if (count === 0) return null;

  return (
    <button
      className={`finding-badge ${severity}`}
      onClick={onClick}
      title={`${count} ${severity} finding${count > 1 ? 's' : ''}`}
    >
      <span className="badge-count">{count}</span>
      <span className="badge-label">{severity}</span>
    </button>
  );
};

// ============================================================================
// Main Component
// ============================================================================

export const ComplianceLensCard: React.FC<ComplianceLensCardProps> = ({
  journeyId,
  result: externalResult,
  onRunAssessment,
  onViewDetails,
  onViewFindings,
  compact = false,
  loading: externalLoading = false,
}) => {
  // Use hook if no external result provided
  const hookData = useComplianceLens(journeyId, { autoFetch: !externalResult });
  const {
    result: hookResult,
    loading: hookLoading,
    error,
    isRunning,
    runAssessment,
    fetchResult,
  } = hookData;

  // Use external result if provided, otherwise use hook result
  const result = externalResult || hookResult;
  const loading = externalLoading || hookLoading;

  // Handle run assessment
  const handleRunAssessment = async () => {
    if (onRunAssessment) {
      onRunAssessment();
      return;
    }
    await runAssessment();
  };

  // Loading state
  if (loading) {
    return (
      <div className={`compliance-lens-card ${compact ? 'compact' : ''} loading`}>
        <div className="compliance-card-header">
          <div className="compliance-card-icon">
            <span role="img" aria-label="Privacy">&#x1F512;</span>
          </div>
          <h3 className="compliance-card-title">Privacy Compliance</h3>
        </div>
        <div className="compliance-card-content">
          <div className="compliance-loading-skeleton">
            <div className="skeleton-gauge" />
            <div className="skeleton-regulations">
              <div className="skeleton-reg" />
              <div className="skeleton-reg" />
              <div className="skeleton-reg" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className={`compliance-lens-card ${compact ? 'compact' : ''}`}>
        <div className="compliance-card-header">
          <div className="compliance-card-icon">
            <span role="img" aria-label="Privacy">&#x1F512;</span>
          </div>
          <h3 className="compliance-card-title">Privacy Compliance</h3>
        </div>
        <div className="compliance-error-state">
          <p>Failed to load compliance assessment</p>
          <button className="compliance-retry-btn" onClick={fetchResult}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Empty state - no assessment yet
  if (!result) {
    return (
      <div className={`compliance-lens-card ${compact ? 'compact' : ''}`}>
        <div className="compliance-card-header">
          <div className="compliance-card-icon">
            <span role="img" aria-label="Privacy">&#x1F512;</span>
          </div>
          <div className="compliance-card-title-group">
            <h3 className="compliance-card-title">Privacy Compliance</h3>
            <span className="compliance-card-subtitle">GDPR / HIPAA / CCPA Assessment</span>
          </div>
        </div>
        <div className="compliance-empty-state">
          <p>No compliance assessment available</p>
          <button
            className="empty-btn"
            onClick={handleRunAssessment}
            disabled={isRunning}
          >
            {isRunning ? 'Running...' : 'Run Compliance Assessment'}
          </button>
        </div>
      </div>
    );
  }

  // Success state - display results
  const complianceInfo = COMPLIANCE_LEVELS[result.overall_level] || COMPLIANCE_LEVELS.not_applicable;
  const { findings_summary } = result;

  return (
    <div className={`compliance-lens-card ${compact ? 'compact' : ''} ${result.overall_level}`}>
      {/* Header */}
      <div className="compliance-card-header">
        <div className="compliance-card-icon">
          <span role="img" aria-label="Privacy">&#x1F512;</span>
        </div>
        <div className="compliance-card-title-group">
          <h3 className="compliance-card-title">Privacy Compliance</h3>
          {!compact && (
            <span className="compliance-card-subtitle">GDPR / HIPAA / CCPA</span>
          )}
        </div>
        <div
          className="compliance-level-badge"
          style={{ backgroundColor: complianceInfo.color }}
          title={complianceInfo.description}
        >
          {complianceInfo.label}
        </div>
        {!compact && (
          <button
            className="compliance-run-btn"
            onClick={handleRunAssessment}
            disabled={isRunning}
            title="Run new assessment"
          >
            {isRunning ? '...' : '▶'}
          </button>
        )}
      </div>

      {/* Main Content */}
      <div className="compliance-card-content">
        {/* Score Section */}
        <div className="compliance-score-section">
          <ScoreGauge score={result.overall_score} size={compact ? 'small' : 'medium'} />
          {!compact && (
            <div className="compliance-score-info">
              <div className="score-level">{complianceInfo.label}</div>
              <p className="score-description">{complianceInfo.description}</p>
            </div>
          )}
        </div>

        {/* Regulations Breakdown */}
        <div className="compliance-regulations-section">
          {!compact && <h4 className="regulations-header">Regulation Scores</h4>}
          <div className="regulations-grid">
            <RegulationCard
              regulation="gdpr"
              score={result.gdpr_score}
              level={result.gdpr_level}
            />
            <RegulationCard
              regulation="hipaa"
              score={result.hipaa_score}
              level={result.hipaa_level}
            />
            <RegulationCard
              regulation="ccpa"
              score={result.ccpa_score}
              level={result.ccpa_level}
            />
          </div>
        </div>

        {/* Findings Summary */}
        {!compact && findings_summary.total > 0 && (
          <div className="compliance-findings-section">
            <h4 className="findings-header">Findings ({findings_summary.total})</h4>
            <div className="findings-badges">
              <FindingsBadge
                severity="critical"
                count={findings_summary.critical}
                onClick={() => onViewFindings?.('critical')}
              />
              <FindingsBadge
                severity="major"
                count={findings_summary.major}
                onClick={() => onViewFindings?.('major')}
              />
              <FindingsBadge
                severity="minor"
                count={findings_summary.minor}
                onClick={() => onViewFindings?.('minor')}
              />
              <FindingsBadge
                severity="observation"
                count={findings_summary.observation}
                onClick={() => onViewFindings?.('observation')}
              />
            </div>
          </div>
        )}

        {/* Compact findings indicator */}
        {compact && findings_summary.total > 0 && (
          <div className="compliance-findings-compact">
            {findings_summary.critical > 0 && (
              <span className="finding-dot critical" title={`${findings_summary.critical} critical`} />
            )}
            {findings_summary.major > 0 && (
              <span className="finding-dot major" title={`${findings_summary.major} major`} />
            )}
            <span className="finding-count">{findings_summary.total} findings</span>
          </div>
        )}
      </div>

      {/* Footer Actions */}
      {!compact && (
        <div className="compliance-card-footer">
          {result.created_at && (
            <span className="compliance-timestamp">
              Last assessed: {formatDate(result.created_at)}
            </span>
          )}
          <div className="compliance-card-actions">
            <button
              className="compliance-action-btn secondary"
              onClick={handleRunAssessment}
              disabled={isRunning}
            >
              {isRunning ? 'Running...' : 'Re-assess'}
            </button>
            {result.id && onViewDetails && (
              <button
                className="compliance-action-btn primary"
                onClick={() => onViewDetails(result.id)}
              >
                View Details
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ComplianceLensCard;
